<template>
    <div class="custom-calendar bg-white p-4 md:p-7 max-[767px]:mb-3 rounded-2xl shadow-sm border border-[var(--bone)]">
        <div class="calendar-header flex items-center justify-between mb-4">
            <button type="button" class="nav-btn border border-[var(--bone)] rounded-xl w-9 h-9 flex items-center justify-center" @click="prevMonth" :disabled="isPrevDisabled"><svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.54443 12.1538L0.935059 6.54443L6.54443 0.935059" stroke="#2C2421" stroke-width="1.86979" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
            <div class="calendar-title text-lg font-medium text-[var(--charcoal-dark)]">
                {{ monthName }} {{ currentYear }}
            </div>
            <button type="button" class="nav-btn border border-[var(--bone)] rounded-xl w-9 h-9 flex items-center justify-center" @click="nextMonth"><svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.935059 12.1538L6.54443 6.54443L0.935059 0.935059" stroke="#2C2421" stroke-width="1.86979" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        </div>

        <div class="calendar-grid grid grid-cols-7 gap-[6px] mb-5">
            <div class="calendar-day text-lg text-[#988881] text-center" v-for="day in weekDays" :key="day">{{ day }}</div>
            <div v-for="blank in blanks" :key="'b' + blank" class="calendar-cell blank"></div>
            <div v-for="day in daysInMonth" :key="'d' + day" class="calendar-cell text-lg text-[var(--charcoal-dark)] border-[2px] border-transparent rounded-xl p-1 text-center cursor-pointer min-h-[36px] flex items-center justify-center" :class="[
                getColorClass(day),
                {  disabled: isDisabled(day) },
                { selected: isSelected(day) },
                { today: isToday(day) },
                isDisabled(day) ? 'pointer-events-none bg-transparent opacity-50' : '',
                isSelected(day) ? '!border-black !bg-white' : '',
                isToday(day) ? '!border-[var(--primary)] !bg-[var(--primary-light)]' : '',
                !hasSlotData(day) && !isDisabled(day) ? 'bg-gray-100 text-gray-400 pointer-events-none' : ''
            ]" data-track="select_date" @click="selectDate(day)">
                {{ day }}
            </div>
        </div>

        <div class="calendar-footer">
            <div class="selected-date text-lg lg:text-[22px] text-[var(--charcoal)] font-bold mb-5">
                <strong class="mb-1">Selected Date:</strong>
                <div class="text-lg">{{ formattedSelectedDate }}</div>
            </div>
            <div class="color-legend">
                <div class="flex items-center mb-1">
                    <span class="w-5 h-5 mr-3 rounded-sm border border-neutral-300 shrink-0 shadow-sm bg-amber-500/15 text-amber-700"></span> Low Booking (≤ 30%)
                </div>
                <div class="flex items-center mb-1">
                    <span class="w-5 h-5 mr-3 rounded-sm border border-neutral-300 shrink-0 shadow-sm bg-orange-400/25 text-orange-700"></span> Medium Booking (31–70%)
                </div>
                <div class="flex items-center mb-1">
                    <span class="w-5 h-5 mr-3 rounded-sm border border-neutral-300 shrink-0 shadow-sm bg-orange-400/50 text-orange-700"></span> High Booking (> 70%)
                </div>
                <div class="flex items-center mb-1">
                    <span class="w-5 h-5 mr-3 rounded-sm border border-neutral-300 shrink-0 shadow-sm bg-teal-600/20 text-neutral-900"></span> Available (0% booked)
                </div>
                <div class="flex items-center mb-1">
                    <span class="w-5 h-5 mr-3 rounded-sm border border-neutral-300 shrink-0 shadow-sm" style="outline: 2px solid black; background: white;"></span> Selected Date
                </div>
                <div class="flex items-center mb-1">
                    <span class="w-5 h-5 mr-3 rounded-sm border border-neutral-300 shrink-0 shadow-sm disabled-box"></span> Not Available / No Slots
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { getMomentTimezone } from '../../utils/dateUtils';

export default {
    name: 'CustomCalendar',
    props: ['modelValue', 'form'],
    emits: ['update:modelValue', 'selected'],
    data() {
        const today = getMomentTimezone(this.$store.state.timezone);
        const isDixies = this.$store.state.tenantId === 'dixies' && today.year() === 2025;

        return {
            currentMonth: isDixies ? 0 : today.month(),  // Jan if Dixies
            currentYear: isDixies ? 2026 : today.year(), // 2026 if Dixies
            selected: this.modelValue,
            colorDates: {},
            minYear: isDixies ? 2026 : today.year(),
            minMonth: isDixies ? 0 : today.month(),
            autoSelectPending: true,
            autoSelectMonthsChecked: 0
        };
    },
    mounted() {
        // If form has a date, navigate calendar to that month
        if (this.form?.date) {
            const formDate = getMomentTimezone(this.$store.state.timezone, this.form.date);
            const isDixies = this.$store.state.tenantId === 'dixies' && getMomentTimezone(this.$store.state.timezone).year() === 2025;

            this.currentMonth = isDixies ? 0 : formDate.month();
            this.currentYear = isDixies ? 2026 : formDate.year();
        }
        this.fetchColorDates();
    },
    watch: {
        currentMonth() {
            this.fetchColorDates();
        },
        currentYear() {
            this.fetchColorDates();
        },
        colorDates() {
            if (this.autoSelectPending) {
                this.tryAutoSelectNextAvailable();
            }
        }
    },
    computed: {
        daysInMonth() {
            return getMomentTimezone(this.$store.state.timezone, [this.currentYear, this.currentMonth]).daysInMonth();
        },
        blanks() {
            return getMomentTimezone(this.$store.state.timezone, [this.currentYear, this.currentMonth, 1]).day(); // day of week (0-6)
        },
        weekDays() {
            return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
        },
        monthName() {
            return getMomentTimezone(this.$store.state.timezone, [this.currentYear, this.currentMonth]).format('MMMM');
        },
        isPrevDisabled() {
            const today = getMomentTimezone(this.$store.state.timezone);

            if (this.$store.state.tenantId === 'dixies' && today.year() === 2025) {
                return (
                    (this.currentYear < this.minYear) ||
                    (this.currentYear === this.minYear && this.currentMonth <= this.minMonth)
                );
            }

            // default logic for others
            return (
                this.currentYear === today.year() &&
                this.currentMonth === today.month()
            );
        },
        formattedSelectedDate() {
            // Use modelValue (the selected date) instead of form.date for reactivity
            const selectedDate = this.modelValue || this.form?.date;
            if (!selectedDate) {
                return 'No date selected';
            }
            return getMomentTimezone(this.$store.state.timezone, selectedDate).format('dddd, MMMM D, YYYY');
        }
    },
    methods: {
        async fetchColorDates() {
            const month = this.currentMonth + 1;
            const year = this.currentYear;
            const { package_id, affiliate_id } = this.form;

            try {
                const response = await axios.get(`/get-color-date/${month}/${year}/${package_id}/${affiliate_id}`);
                const data = response.data;
                const colored = {};

                // If API returns empty array or no data, reset colorDates to empty object
                if (Array.isArray(data) && data.length === 0) {
                    this.colorDates = {};
                    return;
                }

                for (const [date, info] of Object.entries(data)) {
                    // Use backend-calculated CSS class and disabled state
                    colored[date] = {
                        color: info.css_class || null,
                        disabled: info.disabled || false
                    };
                }

                this.colorDates = colored;
            } catch (err) {
                console.error('Failed to fetch color dates:', err);
                // Reset colorDates on error
                this.colorDates = {};
            }
        },
        dateKey(day) {
            return `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        },
        isDisabled(day) {
            const dateKey = this.dateKey(day);
            const data = this.colorDates[dateKey];

            // If no slot data is available for this date, disable it
            if (!this.hasSlotData(day)) {
                return true;
            }

            // Use backend-calculated disabled state if available
            if (data && typeof data.disabled !== 'undefined') {
                return data.disabled;
            }

            const today = getMomentTimezone(this.$store.state.timezone);
            const cellDate = getMomentTimezone(this.$store.state.timezone, [this.currentYear, this.currentMonth, day]);

            if (this.$store.state.tenantId === 'dixies' && today.year() === 2025) {
                // Block all before Jan 2026
                const minDate = getMomentTimezone(this.$store.state.timezone, [this.minYear, this.minMonth, 1]);
                return cellDate < minDate;
            } else {
                // Existing past-date logic
                const dateParts = this.getDateParts();
                const tzYear = parseInt(dateParts.year);
                const tzMonth = parseInt(dateParts.month) - 1;
                const tzDay = parseInt(dateParts.day);
                const todayDate = getMomentTimezone(this.$store.state.timezone, [tzYear, tzMonth, tzDay]);
                return cellDate < todayDate;
            }
        },
        hasSlotData(day) {
            const dateKey = this.dateKey(day);
            return this.colorDates.hasOwnProperty(dateKey);
        },
        isSelected(day) {
            return this.modelValue === this.dateKey(day);
        },
        getColorClass(day) {
            const dateKey = this.dateKey(day);
            const data = this.colorDates[dateKey];

            // If no slot data exists, return empty (will be handled by template)
            if (!this.hasSlotData(day)) {
                return '';
            }

            // Return the color class from API or default available color
            return data?.color || 'bg-teal-600/20';
        },
        isToday(day) {
            const dateParts = this.getDateParts();

            const tzYear = parseInt(dateParts.year);
            const tzMonth = parseInt(dateParts.month) - 1;
            const tzDay = parseInt(dateParts.day);

            return (
                day === tzDay &&
                this.currentMonth === tzMonth &&
                this.currentYear === tzYear
            );
        },
        selectDate(day) {
            const date = this.dateKey(day);
            if (!this.isDisabled(day)) {
                this.$emit('update:modelValue', date);
                this.$emit('selected', date);
            }
        },
        prevMonth() {
            if (this.currentMonth === 0) {
                this.currentMonth = 11;
                this.currentYear--;
            } else {
                this.currentMonth--;
            }
        },
        nextMonth() {
            if (this.currentMonth === 11) {
                this.currentMonth = 0;
                this.currentYear++;
            } else {
                this.currentMonth++;
            }
        },
        tryAutoSelectNextAvailable() {
            const currentlySelected = this.modelValue;
            const isCurrentAvailable = currentlySelected &&
                this.colorDates[currentlySelected] &&
                !this.colorDates[currentlySelected].disabled;

            if (isCurrentAvailable) {
                this.autoSelectPending = false;
                return;
            }

            if (this.autoSelectMonthsChecked >= 12) {
                this.autoSelectPending = false;
                return;
            }

            const todayStr = getMomentTimezone(this.$store.state.timezone).format('YYYY-MM-DD');
            const availableDates = Object.entries(this.colorDates)
                .filter(([date, info]) => !info.disabled && date >= todayStr)
                .map(([date]) => date)
                .sort();

            if (availableDates.length > 0) {
                const nextDate = availableDates[0];
                this.$emit('update:modelValue', nextDate);
                this.$emit('selected', nextDate);
                this.autoSelectPending = false;
            } else {
                this.autoSelectMonthsChecked++;
                this.nextMonth();
            }
        },
        getDateParts() {
            const m = getMomentTimezone(this.$store.state.timezone);

            return {
                year: m.format('YYYY'),
                month: m.format('MM'),
                day: m.format('DD')
            };
        }
    }
};
</script>

<style scoped>


.blank {
    background: transparent;
    pointer-events: none;
}

.calendar-title {
    flex: 1;
    text-align: center;
    font-size: var(--text-lg);
    font-weight: var(--font-bold);
    color: var(--neutral-900);
}

.nav-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.calendar-footer {
    margin-top: var(--space-6);
    padding: var(--space-5);
    background: linear-gradient(135deg, var(--neutral-50) 0%, white 100%);
    border: 1px solid var(--neutral-200);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-sm);
}

.color-legend {
    font-size: var(--text-sm);
    color: var(--neutral-700);
}

.disabled-box {
    background-color: var(--neutral-100) !important;
    border: 1px dashed var(--neutral-400);
}

.calendar-cell.disabled {
    background-color: #f3f4f6 !important;
    color: #9ca3af !important;
    cursor: not-allowed !important;
}
</style>